import { Box, Button, Typography, Paper } from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useTranslation } from 'react-i18next';
import type { AssistantMessage } from '@/stores/assistantStore';
import {
  GUIDE_NOTICE,
  translateGuideKey,
  useGuideStore,
  type GuideTranslator,
} from '@/lib/guides/engine';
import { AssistantMarkdown } from './AssistantMarkdown';
import { QuoteResultAction } from './QuoteResultAction';
import { boundQuoteResult } from '@/lib/quoteResultAction';
import { assistantTurnText, isStructuredTurn } from './settledText';

const SENT_RECEIPT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/;

/**
 * The sent receipt time as written: its own civil date and time and the
 * offset it carried (the workshop's), never re-zoned to this browser.
 */
function sentReceiptParts(iso: string): { date: string; time: string; offset: string } | undefined {
  const match = SENT_RECEIPT.exec(iso);
  if (!match) return undefined;
  const [, y, mo, d, h, mi, zone] = match;
  return {
    date: `${Number(d)}.${Number(mo)}.${y}`,
    time: `${h}:${mi}`,
    offset: zone === 'Z' ? '+00:00' : (zone ?? ''),
  };
}

/**
 * The sent-receipt line by OUTCOME (#2821): "already completed" only for an
 * `ok` replay; an `unknown` settlement may or may not have recorded the
 * time; a `failed` one did not apply it.
 */
function receiptLineKey(message: AssistantMessage): string {
  if (message.outcome === 'unknown') return 'confirmation.receiptMaybeRecorded';
  if (message.outcome === 'failed') return 'confirmation.receiptNotApplied';
  return message.alreadyCompleted ? 'confirmation.receiptAlreadyCompleted' : 'confirmation.receiptSent';
}

interface ChatMessageProps {
  message: AssistantMessage;
}

function asGuideTranslator(translate: unknown): GuideTranslator {
  const call = translate as (key: string, options?: object) => unknown;
  return (key, options) => call(key, options);
}

function guideLine(
  t: GuideTranslator,
  key: string | undefined,
  params: Record<string, string | number | boolean> | undefined,
  fallback: GuideTranslator,
): string | undefined {
  if (!key) return undefined;
  return (
    translateGuideKey(t, key, params) ??
    translateGuideKey(fallback, GUIDE_NOTICE.missingText) ??
    translateGuideKey(fallback, 'errorMessage') ??
    ''
  );
}

function GuideTurn({ message }: { message: AssistantMessage }) {
  const { t } = useTranslation('assistant');
  const { t: guideT } = useTranslation('guides', { useSuspense: false });
  const assistant = asGuideTranslator(t);
  const guides = asGuideTranslator(guideT);
  const params = message.guideParams;
  const notice = guideLine(assistant, message.guideNoticeKey, params, assistant);
  const body = guideLine(guides, message.guideTextKey, params, assistant);
  const showAnchorActions = message.guideNoticeKey === GUIDE_NOTICE.anchorNotFound;

  return (
    <Box sx={{ wordBreak: 'break-word' }} data-testid="guide-message">
      {notice && body ? (
        <Typography variant="caption" color="text.secondary" component="p">
          {notice}
        </Typography>
      ) : null}
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {body ?? notice ?? ''}
      </Typography>
      {showAnchorActions && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button size="small" variant="outlined" onClick={() => useGuideStore.getState().skip()}>
            {t('guide.skip')}
          </Button>
          <Button size="small" onClick={() => useGuideStore.getState().cancel()}>
            {t('guide.stop')}
          </Button>
        </Box>
      )}
    </Box>
  );
}

export function ChatMessage({ message }: ChatMessageProps) {
  const { t } = useTranslation('assistant');
  const isUser = message.role === 'user';
  const sentReceipt =
    message.type === 'action_confirmed' && message.pinnedReceivedAt
      ? sentReceiptParts(message.pinnedReceivedAt)
      : undefined;

  const quoteResult =
    message.type === 'action_confirmed'
      ? boundQuoteResult(message.quoteResult, {
          outcome: message.outcome,
          conversationId: message.conversationId,
          actionId: message.actionId,
        })
      : undefined;
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        mb: 1.5,
        flexDirection: isUser ? 'row-reverse' : 'row',
        alignItems: 'flex-start',
      }}
    >
      <Box
        sx={(theme) => ({
          width: 28,
          height: 28,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isUser
            ? theme.palette.primary.main
            : theme.palette.mode === 'dark'
              ? theme.palette.grey[700]
              : theme.palette.grey[200],
          color: isUser ? theme.palette.primary.contrastText : theme.palette.text.secondary,
          flexShrink: 0,
          mt: 0.5,
        })}
      >
        {isUser ? (
          <PersonOutlineIcon sx={{ fontSize: 16 }} />
        ) : (
          <SmartToyOutlinedIcon sx={{ fontSize: 16 }} />
        )}
      </Box>

      <Paper
        elevation={0}
        sx={(theme) => ({
          px: 1.5,
          py: 1,
          maxWidth: '80%',
          backgroundColor: isUser
            ? theme.palette.primary.main
            : theme.palette.mode === 'dark'
              ? theme.palette.grey[800]
              : theme.palette.grey[100],
          color: isUser ? theme.palette.primary.contrastText : theme.palette.text.primary,
          borderRadius: 2,
          borderTopRightRadius: isUser ? 0 : undefined,
          borderTopLeftRadius: isUser ? undefined : 0,
        })}
      >
        {isUser ? (
          <Typography
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {message.content}
          </Typography>
        ) : message.type === 'guide' ? (
          <GuideTurn message={message} />
        ) : message.plain === true || isStructuredTurn(message) ? (
          // Plain text on purpose: interpolated params and a card's
          // description are data, and data must not become a link or an
          // image (see `isStructuredTurn`, `AssistantMessage.plain`). A blank
          // line still separates paragraphs, as the Markdown path did.
          <Box sx={{ wordBreak: 'break-word' }}>
            {assistantTurnText(t, message)
              .split(/\n{2,}/)
              .map((paragraph, index) => (
                <Typography key={index} variant="body2" sx={{ my: 0.5, whiteSpace: 'pre-wrap' }}>
                  {paragraph}
                </Typography>
              ))}
          </Box>
        ) : (
          <AssistantMarkdown content={assistantTurnText(t, message)} />
        )}
        {quoteResult && <QuoteResultAction action={quoteResult} />}
        {sentReceipt && (
          <Typography variant="caption" color="text.secondary" component="p" data-testid="sent-receipt-time">
            {t(receiptLineKey(message), sentReceipt)}
          </Typography>
        )}
      </Paper>
    </Box>
  );
}
